import React, { useEffect, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';

import {
  Dialog, DialogHeader, DialogTitle, DialogDescription, DialogContent, DialogFooter,
  Button, Input, Select, AreaChip,
} from '../ui-v2';
import IconPicker from './components/IconPicker';
import { getIcon } from '../inventario/icons';
import { crearArea, editarArea } from './api';

/**
 * SoporteAreaDialog — modal de crear / editar área de incidencia.
 *
 * - Crear: campos nombre (obligatorio), icono, color. El slug lo deriva el backend.
 * - Editar: mismos campos. El slug NO se edita (bloqueado por contrato del endpoint).
 * - Preview en vivo del AreaChip con el color y nombre escritos.
 * - Maneja el 409 slug_duplicado mostrando el aviso junto al campo Nombre.
 *
 * Props: { open, onClose, area (null = crear), onSaved }
 */

// Los 8 tokens de la paleta funcional DS v2.5 que soporta AreaChip.
const TOKENS_COLOR = [
  'intranet', 'hardware', 'software', 'accesos',
  'conectividad', 'instalaciones', 'rrhh', 'otros',
];

export default function SoporteAreaDialog({ open, onClose, area, onSaved }) {
  const esEdicion = !!area;

  const [nombre, setNombre] = useState('');
  const [icono, setIcono] = useState('');
  const [color, setColor] = useState('otros');
  const [guardando, setGuardando] = useState(false);
  const [errorNombre, setErrorNombre] = useState(null);
  const [errorGeneral, setErrorGeneral] = useState(null);

  // Reset al abrir / cambiar de área.
  useEffect(() => {
    if (!open) return;
    setNombre(area?.nombre || '');
    setIcono(area?.icono || '');
    setColor(area?.color || 'otros');
    setErrorNombre(null);
    setErrorGeneral(null);
  }, [open, area]);

  async function guardar() {
    const nombreLimpio = nombre.trim();
    if (!nombreLimpio) {
      setErrorNombre('El nombre es obligatorio.');
      return;
    }
    setGuardando(true);
    setErrorNombre(null);
    setErrorGeneral(null);

    const payload = {
      nombre: nombreLimpio,
      icono: icono.trim() || null,
      color: color || null,
    };

    try {
      if (esEdicion) {
        await editarArea(area.id, payload);
      } else {
        await crearArea(payload);
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      const data = e?.response?.data;
      if (e?.response?.status === 409 && data?.error === 'slug_duplicado') {
        setErrorNombre('Ya existe un área con ese slug — prueba con otro nombre.');
      } else {
        setErrorGeneral(data?.message || 'No se pudo guardar el área.');
      }
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} size="default">
      <DialogHeader>
        <DialogTitle>{esEdicion ? 'Editar área' : 'Nueva área'}</DialogTitle>
        <DialogDescription>
          {esEdicion
            ? 'El slug no se puede cambiar para no romper referencias existentes.'
            : 'El slug se genera automáticamente a partir del nombre.'}
        </DialogDescription>
      </DialogHeader>

      <DialogContent>
        <div className="space-y-4 py-1">
          {/* Nombre */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              Nombre <span className="text-red-500">*</span>
            </label>
            <Input
              value={nombre}
              onChange={(e) => { setNombre(e.target.value); setErrorNombre(null); }}
              placeholder="Ej. Hardware"
              autoFocus
            />
            {errorNombre && (
              <p className="mt-1.5 text-xs text-red-600 flex items-start gap-1">
                <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                {errorNombre}
              </p>
            )}
          </div>

          {/* Icono */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              Icono <span className="text-slate-400 font-normal">(opcional)</span>
            </label>
            <IconPicker value={icono} onChange={setIcono} disabled={guardando} />
          </div>

          {/* Color */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              Color <span className="text-slate-400 font-normal">(token de la paleta)</span>
            </label>
            <Select value={color} onChange={(e) => setColor(e.target.value)}>
              {TOKENS_COLOR.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </Select>
          </div>

          {/* Preview en vivo */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">Vista previa</label>
            <AreaChip
              area={color || 'otros'}
              icon={icono ? React.createElement(getIcon(icono), { className: 'w-3 h-3' }) : null}
            >
              {nombre.trim() || 'Nombre del área'}
            </AreaChip>
          </div>

          {errorGeneral && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-md p-2.5 flex items-start gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              {errorGeneral}
            </div>
          )}
        </div>
      </DialogContent>

      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={guardando}>Cancelar</Button>
        <Button onClick={guardar} disabled={guardando || !nombre.trim()}>
          {guardando ? <><Loader2 className="w-4 h-4 animate-spin" /> Guardando…</> : (esEdicion ? 'Guardar cambios' : 'Crear área')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
